/**
 * 008-reporting-dashboards T018/T020 (FR-013, SC-005). `difference` and `attribution` render the
 * row's own fields verbatim — this file computes no subtraction between them at all.
 *
 * Accessors on every column so the toolbar's search matches and a header click sorts the whole
 * fetched set; each figure sorts on the API's own value read as a number, shown as sent.
 */
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { formatDate } from "../../lib/format";
import { money, attributionLabel, type OrderToInvoiceRow } from "../../lib/services/ordering";
import { orderPath } from "../../lib/paths";

const MONEY = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });
const formatSigned = (value: number | string) => {
  const parsed = money(value);
  if (parsed === null) return "—";
  return MONEY.format(parsed);
};

export const orderToInvoiceColumns: ColumnDef<OrderToInvoiceRow>[] = [
  {
    id: "orderNumber",
    accessorKey: "orderNumber",
    header: "Order",
    // Links to the order detail screen, which already shows invoiceNumber/invoicedTotal — this row
    // carries no invoiceId to link to a dedicated invoice screen (see ordering.ts's own note), and
    // there is no packing-entries-by-order screen in this app yet.
    cell: ({ row }) => (
      <Link to={orderPath(row.original.orderNumber)} className="font-medium hover:underline">
        {row.original.orderNumber}
      </Link>
    ),
  },
  { id: "orderedValue", accessorFn: (row) => money(row.orderedValue) ?? undefined, sortUndefined: "last", header: "Ordered", meta: { align: "right" }, cell: ({ row }) => formatSigned(row.original.orderedValue) },
  { id: "invoicedValue", accessorFn: (row) => money(row.invoicedValue) ?? undefined, sortUndefined: "last", header: "Invoiced", meta: { align: "right" }, cell: ({ row }) => formatSigned(row.original.invoicedValue) },
  { id: "freightExcluded", accessorFn: (row) => money(row.freightExcluded) ?? undefined, sortUndefined: "last", header: "Freight excluded", meta: { align: "right" }, cell: ({ row }) => formatSigned(row.original.freightExcluded) },
  {
    id: "difference",
    accessorFn: (row) => money(row.difference) ?? undefined,
    sortUndefined: "last",
    header: "Difference",
    meta: { align: "right" },
    // The API's own figure, rendered as-is — never orderedValue minus invoicedValue computed here.
    cell: ({ row }) => <span className="font-medium">{formatSigned(row.original.difference)}</span>,
  },
  { id: "attribution", accessorFn: (row) => attributionLabel(row.attribution), header: "Attribution", cell: ({ row }) => attributionLabel(row.original.attribution) },
  // ISO date — sorts chronologically as text.
  { id: "invoicedAt", accessorKey: "invoicedAt", header: "Invoiced", cell: ({ row }) => formatDate(row.original.invoicedAt) },
];
